<template>
  <div>
    <Breadcrumbs main="Accounts" title="Payments" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Payments</h5>
                <span class="text-muted">Record client payments and allocate them to matter bills</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadPayments"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Add Payment
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading payments...
              </div>
              <div v-else-if="payments.length === 0" class="text-center py-4 text-muted">
                No payments found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Payment No.</th>
                      <th>Date</th>
                      <th>Client</th>
                      <th class="text-end">Amount</th>
                      <th>Payment mode</th>
                      <th>Reference</th>
                      <th>Items</th>
                      <th>Created</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="payment in payments" :key="payment.paymentId">
                      <td>
                        <strong>{{ payment.paymentNo || `#${payment.paymentId}` }}</strong>
                      </td>
                      <td>{{ formatDate(payment.paymentDate) }}</td>
                      <td>{{ clientName(payment.clientId) }}</td>
                      <td class="text-end">{{ formatMoney(payment.amount) }}</td>
                      <td>{{ payment.paymentMode || '—' }}</td>
                      <td>{{ payment.paymentModeNo || '—' }}</td>
                      <td>{{ paymentItemSummary(payment) }}</td>
                      <td>{{ formatDate(payment.createdAt) }}</td>
                      <td class="text-end">
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(payment)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canDelete"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(payment)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Payment' : 'Add Payment' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Payment number</label>
                      <input
                        v-model="form.paymentNo"
                        class="form-control"
                        type="text"
                        maxlength="100"
                        placeholder="e.g. PAY-2026-001"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Payment date</label>
                      <input
                        v-model="form.paymentDate"
                        class="form-control"
                        type="date"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Client <span class="text-danger">*</span></label>
                      <select
                        v-model.number="form.clientId"
                        class="form-select"
                        :required="!isEditing"
                        :disabled="isEditing"
                        @change="onClientChange"
                      >
                        <option :value="null" disabled>Select a client</option>
                        <option
                          v-for="client in clients"
                          :key="client.clientId"
                          :value="client.clientId"
                        >
                          {{ client.fullName || `Client #${client.clientId}` }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Payment mode</label>
                      <select v-model="form.paymentMode" class="form-select">
                        <option value="">Select mode</option>
                        <option v-for="mode in paymentModeOptions" :key="mode" :value="mode">
                          {{ mode }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Reference no.</label>
                      <input
                        v-model="form.paymentModeNo"
                        class="form-control"
                        type="text"
                        maxlength="100"
                        placeholder="Cheque or transaction number"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Total amount</label>
                      <input
                        class="form-control"
                        type="text"
                        :value="formatMoney(lineItemsTotal)"
                        readonly
                      >
                    </div>
                  </div>
                </div>

                <div class="mt-3">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <label class="form-label mb-0">Line items <span class="text-danger">*</span></label>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="!form.clientId"
                      @click="addLineItem"
                    >
                      <vue-feather type="plus" class="me-1" />
                      Add item
                    </button>
                  </div>
                  <p v-if="!form.clientId" class="text-muted f-12 mb-2">
                    Select a client to allocate payments to bills.
                  </p>
                  <div v-else-if="form.paymentItems.length === 0" class="text-muted f-12 mb-2">
                    Add at least one line item allocating payment to a bill.
                  </div>
                  <div
                    v-for="(item, index) in form.paymentItems"
                    :key="index"
                    class="row align-items-end mb-2"
                  >
                    <div class="col-md-6">
                      <label v-if="index === 0" class="form-label">Bill</label>
                      <select
                        v-model.number="item.billId"
                        class="form-select"
                        required
                      >
                        <option :value="null" disabled>Select bill</option>
                        <option
                          v-for="bill in selectableBills(item.billId)"
                          :key="bill.billId"
                          :value="bill.billId"
                        >
                          {{ billLabel(bill) }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label v-if="index === 0" class="form-label">Amount</label>
                      <input
                        v-model.number="item.amount"
                        class="form-control"
                        type="number"
                        min="0.01"
                        step="0.01"
                        :max="maxItemAmount(item)"
                        required
                      >
                      <small v-if="item.billId" class="text-muted">
                        Max: {{ formatMoney(maxItemAmount(item)) }}
                      </small>
                    </div>
                    <div class="col-md-2">
                      <button
                        type="button"
                        class="btn btn-outline-danger w-100"
                        title="Remove item"
                        @click="removeLineItem(index)"
                      >
                        <vue-feather type="trash-2" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Payment') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="paymentToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Payment</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="paymentToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete payment
                <strong>{{ paymentToDelete.paymentNo || `#${paymentToDelete.paymentId}` }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                Bill payments from this payment will be reversed. The record is retained for audit.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="paymentToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="paymentToDelete" class="modal-backdrop fade show" @click="paymentToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listClients } from '@/services/clientService'
import { listBills } from '@/services/billService'
import { listMatters } from '@/services/matterService'
import {
  listPayments,
  createPayment,
  updatePayment,
  deletePayment,
} from '@/services/paymentService'

const PAYMENT_MODE_OPTIONS = ['CASH', 'CHEQUE', 'BANK_TRANSFER']

function hasPaymentPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const paymentPriv = user.privileges.find((p) => p.entity === 'PAYMENT')
  if (!paymentPriv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return paymentPriv[map[action]] === true
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10)
}

const emptyLineItem = () => ({
  billId: null,
  amount: null,
})

const emptyForm = () => ({
  paymentNo: '',
  paymentDate: todayIsoDate(),
  clientId: null,
  paymentMode: '',
  paymentModeNo: '',
  paymentItems: [],
})

export default {
  name: 'AccountsPayments',

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasPaymentPrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasPaymentPrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasPaymentPrivilege(this.currentUser, 'delete')
    },

    paymentModeOptions() {
      return PAYMENT_MODE_OPTIONS
    },

    clientMatterIds() {
      if (!this.form.clientId) return new Set()
      return new Set(
        this.matters
          .filter((m) => m.clientId === this.form.clientId)
          .map((m) => m.matterId),
      )
    },

    clientBills() {
      if (!this.form.clientId) return []
      return this.bills.filter((bill) => this.clientMatterIds.has(bill.matterId))
    },

    lineItemsTotal() {
      return this.form.paymentItems.reduce((sum, item) => {
        const amount = Number(item.amount)
        return sum + (Number.isFinite(amount) ? amount : 0)
      }, 0)
    },
  },

  data() {
    return {
      payments: [],
      clients: [],
      bills: [],
      matters: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      originalPaymentItems: [],
      form: emptyForm(),
      formError: '',
      saving: false,
      paymentToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadPayments()
    this.loadClients()
    this.loadBills()
    this.loadMatters()
  },

  methods: {
    async loadPayments() {
      this.loading = true
      this.error = ''

      try {
        const data = await listPayments()
        this.payments = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view payments.'
        } else {
          this.error = err.message || 'Failed to load payments'
        }
      } finally {
        this.loading = false
      }
    },

    async loadClients() {
      try {
        const data = await listClients()
        this.clients = Array.isArray(data) ? data : []
      } catch {
        this.clients = []
      }
    },

    async loadBills() {
      try {
        const data = await listBills()
        this.bills = Array.isArray(data) ? data : []
      } catch {
        this.bills = []
      }
    },

    async loadMatters() {
      try {
        const data = await listMatters()
        this.matters = Array.isArray(data) ? data : []
      } catch {
        this.matters = []
      }
    },

    clientName(clientId) {
      const client = this.clients.find((c) => c.clientId === clientId)
      return client?.fullName || `#${clientId}`
    },

    billLabel(bill) {
      const no = bill.invoiceNo || `#${bill.billId}`
      const balance = this.formatMoney(bill.balance)
      return `${no} — balance ${balance}`
    },

    paymentItemSummary(payment) {
      const items = payment.paymentItems || []
      if (items.length === 0) return '—'
      if (items.length === 1) {
        const bill = this.bills.find((b) => b.billId === items[0].billId)
        const label = bill?.invoiceNo || `#${items[0].billId}`
        return `${label} (${this.formatMoney(items[0].amount)})`
      }
      return `${items.length} bills`
    },

    originalItemAmount(billId) {
      const original = this.originalPaymentItems.find((item) => item.billId === billId)
      return original ? Number(original.amount) || 0 : 0
    },

    availableBalance(billId) {
      const bill = this.bills.find((b) => b.billId === billId)
      if (!bill) return 0
      const balance = Number(bill.balance) || 0
      if (!this.isEditing) return balance
      return balance + this.originalItemAmount(billId)
    },

    selectableBills(currentBillId) {
      const usedIds = new Set(
        this.form.paymentItems
          .map((item) => item.billId)
          .filter((id) => id != null && id !== currentBillId),
      )

      return this.clientBills.filter((bill) => {
        if (usedIds.has(bill.billId)) return false
        if (bill.billId === currentBillId) return true
        return this.availableBalance(bill.billId) > 0
      })
    },

    maxItemAmount(item) {
      if (!item.billId) return undefined
      return this.availableBalance(item.billId)
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    formatMoney(value) {
      if (value == null || Number.isNaN(Number(value))) return '—'
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    onClientChange() {
      this.form.paymentItems = []
    },

    addLineItem() {
      this.form.paymentItems.push(emptyLineItem())
    },

    removeLineItem(index) {
      this.form.paymentItems.splice(index, 1)
    },

    async openCreate() {
      await Promise.all([this.loadBills(), this.loadMatters()])
      this.isEditing = false
      this.editingId = null
      this.originalPaymentItems = []
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    async openEdit(payment) {
      await Promise.all([this.loadBills(), this.loadMatters()])
      this.isEditing = true
      this.editingId = payment.paymentId
      this.originalPaymentItems = (payment.paymentItems || []).map((item) => ({ ...item }))
      this.form = {
        paymentNo: payment.paymentNo || '',
        paymentDate: payment.paymentDate || todayIsoDate(),
        clientId: payment.clientId,
        paymentMode: payment.paymentMode || '',
        paymentModeNo: payment.paymentModeNo || '',
        paymentItems: (payment.paymentItems || []).map((item) => ({
          billId: item.billId,
          amount: item.amount ?? null,
        })),
      }
      if (this.form.paymentItems.length === 0) {
        this.addLineItem()
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    validateLineItems() {
      if (!this.form.clientId) {
        return 'Please select a client.'
      }

      if (this.form.paymentItems.length === 0) {
        return 'Add at least one line item.'
      }

      for (const item of this.form.paymentItems) {
        if (!item.billId) {
          return 'Each line item must reference a bill.'
        }
        const amount = Number(item.amount)
        if (!Number.isFinite(amount) || amount <= 0) {
          return 'Each line item must have a positive amount.'
        }
        const max = this.availableBalance(item.billId)
        if (amount > max) {
          return `Payment amount exceeds available balance for bill ${item.billId}.`
        }
      }

      const billIds = this.form.paymentItems.map((item) => item.billId)
      if (new Set(billIds).size !== billIds.length) {
        return 'Each bill can only appear once per payment.'
      }

      return ''
    },

    buildPayload() {
      const payload = {}

      const paymentNo = this.form.paymentNo?.trim()
      if (paymentNo) payload.paymentNo = paymentNo

      if (this.form.paymentDate) payload.paymentDate = this.form.paymentDate

      if (!this.isEditing && this.form.clientId) {
        payload.clientId = this.form.clientId
      }

      const paymentMode = this.form.paymentMode?.trim()
      if (paymentMode) payload.paymentMode = paymentMode

      const paymentModeNo = this.form.paymentModeNo?.trim()
      if (paymentModeNo) payload.paymentModeNo = paymentModeNo

      payload.paymentItems = this.form.paymentItems.map((item) => ({
        billId: item.billId,
        amount: Number(item.amount),
      }))

      return payload
    },

    async submitForm() {
      this.formError = ''

      const validationError = this.validateLineItems()
      if (validationError) {
        this.formError = validationError
        return
      }

      this.saving = true

      try {
        if (this.isEditing) {
          await updatePayment({
            paymentId: this.editingId,
            ...this.buildPayload(),
          })
        } else {
          await createPayment(this.buildPayload())
        }
        this.showFormModal = false
        await Promise.all([this.loadPayments(), this.loadBills()])
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(payment) {
      this.paymentToDelete = payment
      this.actionError = ''
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deletePayment(this.paymentToDelete.paymentId)
        this.paymentToDelete = null
        await Promise.all([this.loadPayments(), this.loadBills()])
      } catch (err) {
        this.actionError = err.message || 'Failed to delete payment'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
